import { FC } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemText,
  Link,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/home/organisms/Meta';
import { Terms as TermsTemplate } from '@bizapp-lp/home/templates/Terms';

const useStyles = makeStyles((theme) =>
  createStyles({
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
    wordBreak: {
      wordBreak: 'break-word',
    },
  }),
);

const title =
  'HUE Works Suite / HUE Works Suite DX Solutions利用規約 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、HUE Works Suite / HUE Works Suite DX Solutions利用規約ページです。';

const Terms: FC = () => {
  const classes = useStyles();
  const { from: paramFrom } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const from = query.get('from') ?? paramFrom;

  const validatedFrom = [
    'ess',
    'ebm',
    'chatbot',
    'pjr',
    'pjb',
    'workflow',
  ].includes(from)
    ? `${from}`
    : '';

  interface ListTextsProps {
    texts: string[];
  }

  const ListTexts: FC<ListTextsProps> = ({ texts }) => {
    return (
      <>
        {texts.map((t, idx) => {
          return (
            <ListItem key={idx}>
              <ListItemText>{t}</ListItemText>
            </ListItem>
          );
        })}
      </>
    );
  };

  const pageTitle = 'HUE Works Suite / HUE Works Suite DX Solutions利用規約';

  const TermsContents: FC = () => {
    return (
      <>
        <Typography>
          これは過去のHUE Works Suite / HUE Works Suite DX
          Solutions利用規約です。最新版は
          <Link href="https://www.worksap.co.jp/services/spec/terms/">
            こちら
          </Link>
          です。
        </Typography>
        <Typography variant="h2">第1条（利用規約の適用）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社（株式会社ワークスアプリケーションズ・エンタープライズ又は株式会社ワークスアプリケーションズ・システムズのうち、第5条第1項乃至第3項の申込みに対して承諾した法人をいいます。）は、この利用規約（以下「本規約」といいます。）に基づき当社サービス（第2条で定義します。）を提供します。',
              '当社サービスには、別途当社が定めて通知する特則（以下「特則」といいます。）が適用されることがあります。その場合、特則に提示されている事項については特則が本規約に優先して適用され、特則に提示されていない事項については本規約が適用されます。',
              '当社サービスは、本規約（特則を含みます。以下同じとします。）に同意した方のみが利用できるものとし、契約者（第2条で定義します。）は、当社サービスの利用にあたり本規約を遵守するものとします。',
              '当社サービスに関し、別途当社及び契約者の間で締結する契約書、覚書その他の合意がある場合には、当該契約書、覚書その他の合意の規定が本規約に優先して適用されるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第2条（用語の定義）</Typography>
        <Typography>
          本規約において使用する以下の用語の意味は以下に定めるものとします。
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者：本規約に基づき当社と本契約を締結した法人、団体又は個人を意味します。但し、個人が契約者となることが認められる本サービス又は本トライアルサービスは、該当する本サービス仕様書又は本トライアルサービス仕様書にその旨が記載されているものに限ります。',
              '契約希望者：当社サービスの利用を希望する法人、団体又は個人を意味します。但し、個人が契約希望者となることが認められる本サービス又は本トライアルサービスは、該当する本サービス仕様書又は本トライアルサービス仕様書にその旨が記載されているものに限ります。',
              'ユーザーID等: 本サービス又は本トライアルサービスの利用にあたり、ユーザーID、パスワード、APIキーその他の契約者が本人であることを確認するために作成される情報を意味します。',
              '契約者登録情報：契約希望者又は契約者が本契約又は利用契約を締結するためにサービス販売サイト又は契約者専用サイトにおいて入力及び登録し、その後必要に応じて契約者専用サイトで追加又は変更される申込情報その他の情報を意味します。',
              '契約者保存情報：契約者及び共同利用者（第6条第6項で定義します。）が、本サービス又は本トライアルサービスを利用して当社サービス環境（第8条第2項で定義します。）に記録又は保存する情報を意味します。但し、契約者登録情報を除きます。',
              '契約者関連情報：契約者及び共同利用者（第6条第6項で定義します。）による本サービス又は本トライアルサービスの利用に関連し又はかかる利用の過程で当社サービス環境に記録又は保存される、契約者による本サービス又は本トライアルサービスの利用履歴及びその内容、本サービス又は本トライアルサービスに関する契約者と当社の通信履歴及びその内容その他一切の情報であって、契約者固有の情報を含むものを意味します。但し、契約者登録情報及び契約者保存情報を除きます。',
              '契約者情報：契約者登録情報、契約者保存情報及び契約者関連情報の総称を意味します。',
              '契約者専用サイト：契約者が本サービス又は本トライアルサービスの提供を受けることができる契約者専用のウェブサイトを意味します。',
              'サービス販売サイト：当社サービスの利用申込みを受け付ける当社のウェブサイトを意味します。',
              '当社サービス：ソフトウェアの機能をネットワーク経由で提供する別表に定める個々のサービス又はこれらの総称を意味します。',
              '本サービス：契約者との間で第5条第3項に従い利用契約が成立した当社サービス（当該当社サービスに係る本サービス仕様書に第10条に定めるサポートサービスの記載がある場合における当該サポートサービスを含みます。）を意味します。',
              '本トライアルサービス：契約者との間で第5条第2項に従いトライアル利用契約が成立した当社サービス（当該当社サービスに係る本サービス仕様書に第10条に定めるサポートサービスの記載がある場合における当該サポートサービスを含みます。）を意味します。',
              '本サービス仕様書：契約者との間で第5条第3項に従い利用契約が成立した当社サービスの仕様書としてサービス販売サイトに掲載される文書（当該当社サービスについて契約者との間で第5条第3項に従い利用契約が成立した後の追加及び変更を含みます。）及び契約者専用サイトに掲載される文書（掲載後の追加及び変更を含みます。なお、サービス販売サイトに掲載される文書との間に齟齬がある場合には、契約者専用サイトに掲載される文書が優先します。）の総称を意味します。',
              '本トライアルサービス仕様書：契約者との間で第5条第2項に従いトライアル利用契約が成立した当社サービスの仕様書としてサービス販売サイトに掲載される文書（当該当社サービスについて契約者との間で第5条第2項に従いトライアル利用契約が成立した後の追加及び変更を含みます。）及び契約者専用サイトに掲載される文書（掲載後の追加及び変更を含みます。なお、サービス販売サイトに掲載される文書との間に齟齬がある場合には、契約者専用サイトに掲載される文書が優先します。）の総称を意味します。',
              '本契約：第5条第1項に従って成立する契約を意味します。',
              'トライアル利用契約：第5条第2項に従って本トライアルサービス毎に成立する契約を意味します。',
              '利用契約：第5条第3項に従って本サービス毎に成立する契約を意味します。',
              '損害等：ある事象に起因又は関連して発生する損害、損失及び費用（逸失利益及び間接的な損害及び合理的範囲内の弁護士費用を含みます。）の総称を意味します。',
            ]}
          />
        </List>

        <Typography variant="h2">第3条（通知）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社は、契約者に対し、契約者専用サイト又はサービス販売サイトへの掲載若しくは電子メールの送信又はこれらが困難な場合は当社が適当と判断する方法により、随時必要な事項を通知することができます。',
              '前項の通知は、電子メール又は書面により行う場合には当社が当該通知を発信した時点で、契約者専用サイト又はサービス販売サイトへの掲載により行う場合には当社が当該通知の内容を当該サイトに掲載した時点で、それぞれその効力が発生するものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第4条（本規約等の変更）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社は、契約者の了承を得ることなく、本規約、本サービス仕様書又は本トライアルサービス仕様書の内容を追加、削除又は変更することがあります。当社は、本規約、本サービス仕様書又は本トライアルサービス仕様書の内容の追加、削除又は変更を行うときは、契約者に対し、その対象が本規約又は本サービス仕様書のときは1か月以上の予告期間、その対象が本トライアルサービス仕様書の場合は当社が自らの裁量で決定する合理的な予告期間を置いて当該追加、削除又は変更後の内容を通知するものとし、当該追加、削除又は変更の効力は、当該予告期間満了時をもって生じるものとします。',
              '前項の規定にかかわらず、当社は、緊急を要する場合には、前項に定める事前の通知を行うことなく又は前項に定める期間未満の予告期間を定めて、契約者への通知により本規約、本サービス仕様書又は本トライアルサービス仕様書の内容の追加、削除又は変更を行うことができるものとします。この場合、当該追加、削除又は変更の効力は、当該通知を行った時点又は当該通知に定める時点をもって生じるものとします。',
              '契約者は、前2項に定める通知がなされた後、第1項に定める通知については所定の予告期間内に、第2項に定める通知については当該通知の日から1か月が経過する日までの間に、それぞれ第17条に従って本契約、利用契約及びトライアル利用契約の全部又は一部の解約の手続をとらなかった場合には、当該解約の手続をとらなかった本契約、利用契約又はトライアル利用契約については、前2項に従い通知がなされた追加、削除又は変更後の本規約、本サービス仕様書又は本トライアルサービス仕様書に同意したものとみなします。',
              '第1項及び第2項の規定にかかわらず、サービス販売サイトにて本規約、本サービス仕様書又は本トライアルサービス仕様書の内容の追加、削除又は変更が予告されている期間中に第5条第1項乃至第3項に基づく申込みを行う契約希望者は、本契約、利用契約又はトライアル利用契約が成立した時点から当該予告期間の満了までの期間が第1項又は第2項所定の予告期間に満たない場合であっても、当該予告期間満了時に当該変更等の効力が生じることに同意するものとします。',
              '第1項及び第2項の規定にかかわらず、当社は、本規約、本サービス仕様書及び本トライアルサービス仕様書の内容の追加、削除又は変更であって、軽微なもの及び契約者に特段の不利益を生じないものについては、自らの裁量で随時行うことができるものとします。この場合、当該追加、削除又は変更は、契約者への通知により直ちにその効力を生じるものとします。',
              '前各項の規定にかかわらず、本規約、本サービス仕様書及び本トライアルサービス仕様書の内容の追加、削除又は変更であって、民法、商法その他の法律の公の秩序に関しない規定の適用による場合と比べて契約者の権利を制限又は義務を加重し、かつ、民法第1条第2項に規定する基本原則に反して契約者の利益を一方的に害するものは、契約者の書面による同意のある場合に限り、効力を生じるものとします。但し、第14条に定める利用料金の変更については、第14条の規定が本条に優先するものとします。',
              '当社は、本条に基づく本規約、本サービス仕様書又は本トライアルサービス仕様書の追加、削除又は変更に関して契約者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第5条（本契約、トライアル利用契約及び利用契約の成立）
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本契約は、契約希望者が、本規約の全部に同意の上、サービス販売サイトに設けられたオンライン申込機能を通して当社所定の情報を送信する方法（又は、当社が別途承諾した場合に限り、必要事項を記載した当社所定の申込書を提出する方法）により登録の申込みを行い、当社がこれに対して当該申込みを承諾する旨を契約希望者に通知することによって成立します。本契約の内容は本規約によって規律され、本契約の契約期間内に締結された利用契約にはその時点で有効な本規約の内容が適用されるものとします。',
              'トライアル利用契約は、契約希望者又は契約者が、契約者専用サイト又はサービス販売サイトに設けられたオンライン申込機能を通して当社所定の情報を送信する方法（又は、当社が別途承諾した場合に限り、必要事項を記載した当社所定の申込書を提出する方法）により自らが一定期間無償での利用を希望する当社サービスの利用の申込みを行い、当社がこれに対して当該申込みを承諾する旨を契約希望者又は契約者に通知することによって、本トライアルサービス毎に成立します。',
              '利用契約は、契約希望者又は契約者が、契約者専用サイト又はサービス販売サイトに設けられたオンライン申込機能を通して当社所定の情報を送信する方法（又は、当社が別途承諾した場合に限り、必要事項を記載した当社所定の申込書を提出する方法）により自らが利用を希望する当社サービスの利用の申込みを行い、当社がこれに対して当該申込みを承諾する旨を契約希望者又は契約者に通知することによって、本サービス毎に成立します。',
              '当社は、第1項乃至第3項に基づき申込みを行った契約希望者又は契約者に対し、本人確認その他の目的で必要とする情報の提供を求めることができるものとし、契約希望者及び契約者は、かかる求めに従い当該情報を当社に提供するものとします。また、当社は、第1項乃至第3項に基づき申込みを行った契約希望者又は契約者に対し、理由を開示することなく当該申込みを承諾しないことができるものとし、かかる契約希望者又は契約者は、当社が当該申込みを承諾しないことについて、何らの異議も述べることができないものとします。',
              '当社は、第2項に基づく本トライアルサービスの利用の申込みを承諾する旨を通知する場合には、本トライアルサービスの利用開始日（以下「本トライアルサービス利用開始日」といいます。）を明示し、第3項に基づく本サービスの利用の申込みを承諾する旨を通知する場合には、本サービスの利用開始日（以下「本サービス利用開始日」といいます。）を明示して、これらの通知を行うものとします。',
              '第1項乃至第3項に基づく申込みが、法人その他の団体（以下「法人等」といいます。）を代表して行われるものである場合には、当該申込みを行う個人は、当該法人等を代理又は代表して当該申込みを行うことについて、当該法人等から適法かつ有効な授権を受けてこれを行うものとします。',
              '第1項乃至第3項に基づく申込みに基づく契約者登録情報に虚偽の内容又は不足があったために当社又は第三者が損害を被った場合、契約者及び当該申込みを行った個人はその一切の損害について責任を負うものとします。',
              '当社は、契約希望者又は契約者が第1項乃至第3項に従って行った申込みが、通信環境の不具合その他の理由により当社に到達しなかったことにより契約希望者又は契約者に損害が生じた場合であっても、当該損害について契約希望者及び契約者に対して何らの責任も負わないものとします。',
              '本契約、トライアル利用契約又は利用契約が成立した後は、契約者は、当該本契約、トライアル利用契約及び利用契約に係る第1項乃至第3項の申込みをキャンセルすることはできず、また、当該本契約、トライアル利用契約及び利用契約の条件の変更を求めることはできません。但し、当社が、契約者と協議の上で合理的な理由があると判断した場合には、当社は、当該キャンセル又は条件の変更を認めることができるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第6条（本サービスの提供）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社は、利用契約を締結した契約者に対し、本規約、本契約及び利用契約に従い、善良な管理者の注意をもって本サービスを提供するものとします。',
              '本サービスの内容は、本サービス仕様書に定めるとおりとします。本サービス仕様書に記載のない本サービスの内容の詳細は、当社がその裁量によって決定するものとします。',
              '当社は、契約者に対し、本サービス利用開始日までに、本サービス仕様書に定める方法により、ユーザーID等その他本サービスの利用開始にあたり必要となる情報を契約者に対して通知します。',
              '当社が契約者に対して提供する本サービスの提供地域は、日本国内に限るものとします。',
              '本サービスは、本サービス仕様書が定める方法に従って契約者が自らの業務のために利用する目的（以下「本目的」といいます。）のためにのみ提供されるものであり、契約者による再販売、第三者への提供、研究開発その他本目的以外のいかなる目的のためにも利用することはできません。',
              '前項の規定にかかわらず、本サービス仕様書にて本サービスを契約者と共同して契約者のグループ会社等に利用させることを当社が許諾している場合は、契約者は、当該グループ会社等（以下「共同利用者」といいます。）をして、本目的のためにのみ、かつ、本規約、本契約及び利用契約の定めを共同利用者に遵守させることを条件として、本サービスを利用させることができるものとします。但し、共同利用者による本サービス利用に関連する一切の行為は契約者による行為とみなすこととし、共同利用者による本規約、本契約又利用規約の違反については契約者による違反として、契約者が当社に対して一切の責任を負うものとします。',
              '前項の場合において、当社が本規約、本契約及び利用契約に定める義務並びに責任を負う相手方は契約者のみであり、当社は共同利用者に対しては何ら義務又は責任を負いません。',
              '当社は、本サービスに関する作業の全部又は一部を、当社の責任において第三者に再委託できるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第7条（クラウドコンピューティングサービス）
        </Typography>
        <Typography>
          当社サービスの提供に際しては、Amazon Web Services,
          Inc.（以下「AWS」といいます。）が提供するクラウドコンピューティングサービスが利用されます。契約者は、本サービスを利用するにあたり、AWS所定の「AWSカスタマーアグリーメント」（その名称又は内容に変更が生じた場合には、当該変更後の名称及び内容によるものとします。なお、最新版は
          <Link href={'http://aws.amazon.com/jp/agreement/'}>
            http://aws.amazon.com/jp/agreement/
          </Link>
          に掲載されています。）のうち契約者が遵守すべき事項について同意し、これを遵守するものとします。
        </Typography>

        <Typography variant="h2">
          第8条（本サービスの提供時間帯、一時的な中断）
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本サービスの提供時間帯は、本サービス仕様書に定めるとおりとします。',
              '前項の規定にかかわらず、当社は、本サービスの円滑な運営のために、当社が本サービスを提供するためのサーバー環境（以下「当社サービス環境」といいます。）について、計画的なメンテナンス（以下「計画メンテナンス」といいます。）を実施することがあります。当社は、計画メンテナンスを実施する場合には、必要に応じて、あらかじめその旨を契約者に通知した上で、本サービスの提供を一時的に中断することができるものとします。',
            ]}
          />
          <ListItem>
            <ListItemText>
              第1項の規定にかかわらず、当社は、次の各号のいずれかに該当する場合には、契約者への事前の通知なく、本サービスの提供を一時的に中断することができるものとします。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '当社サービス環境について、緊急のメンテナンスを実施する場合',
                  '電気通信事業者又はAWSの役務が提供されない場合',
                  '当社サービス環境に生じた著しい負荷又は障害により、正常に本サービスを提供することが困難となる場合',
                  '当社サービス環境に対するデータの改ざん、ハッキングその他の事由により、本サービスの提供が契約者又はその他の第三者に損害を及ぼす可能性があると当社が判断した場合',
                  '戦争、紛争、動乱、暴動、天災、感染症の拡大その他の不可抗力又はこれらに伴う対応措置により、本サービスの提供が困難となる場合',
                  '火災、停電、その他の不慮の事故又はこれらに伴う対応措置により、本サービスの提供が困難となる場合',
                  'その他前各号に準じ当社が必要と判断した場合',
                ]}
              />
            </List>
          </ListItem>
          <ListItem>
            <ListItemText>
              当社が第2項又は第3項の定めに従い本サービスの提供を中断した場合であっても、契約者は、当該中断について何らの異議も述べることができないものとします。当社は、当該中断により契約者に損害等（中断が本サービスを提供するために利用されるソフトウェア以外のデータ、ソフトウェア又はハードウェアに与える悪影響に起因又は関連して生じた損害等を含みますが、これらに限られません。以下同じとします。）が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。
            </ListItemText>
          </ListItem>
        </List>

        <Typography variant="h2">第9条（契約者の協力義務）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者は、当社が本サービスを提供するにあたり必要とする情報を求めた場合には、かかる情報を当社に提供するものとします。',
              '契約者は、本サービスの利用にあたり、当社との連絡窓口となる者（以下「担当者」といいます。）を定め、その連絡先情報を当社所定の方法により当社に届け出るものとします。また、契約者は、担当者が変更となった場合は、速やかに変更後の担当者の連絡先情報を届け出るものとします。',
              '本サービスの利用に関する契約者からの当社への連絡は、全て担当者を通じて行うものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第10条（サポートサービス）</Typography>
        <Typography>
          当社は、契約者による本サービスの設定又は利用に関する質問への対応、契約者による第23条第2項に定めるソフトウェアのクライアント環境への導入に関する支援、その他契約者が本サービスを円滑に利用するためのサポートサービスを提供する場合があり、その場合のサービス料金、サービス内容その他の具体的な条件は、本サービス仕様書に記載のとおりとします。
        </Typography>

        <Typography variant="h2">第11条（自己責任の原則）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者に対する本サービスの提供は、契約者が当社サービス環境にネットワーク経由で接続することにより行われます。契約者は、当社サービス環境に接続して本サービスを利用するために必要となる通信機器、通信回線（電話利用契約の締結、携帯端末利用契約の締結、インターネットサービスプロバイダへの加入を含みます。）、ソフトウェアその他のコンピュータ環境（以下「クライアント環境」といいます。）を、自己の責任と費用において用意するものとします。',
              '当社は、本サービスを利用するために必要なソフトウェアのクライアント環境へのインストール作業に伴う不具合その他契約者による本サービスの利用に起因又は関連してクライアント環境に生じた不具合により契約者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
              '契約者は、本サービスを利用するために必要な契約者登録情報、ユーザーID等を、自己の責任と費用において管理するものとします。また、契約者は、当該契約者登録情報、ユーザーID等を、方法の如何を問わず第三者に開示し又は利用させてはならないものとします。',
              'ユーザーID等によりなされた本サービスの利用は、当該ユーザーID等を付与された契約者によりなされたものとみなします。当社は、契約者の故意又は過失の有無を問わず、ユーザーID等が契約者以外の第三者に使用されたことにより契約者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
              '契約者は、契約者登録情報に変更があった場合には、当社所定の方法により、遅滞なく当該契約者登録情報の変更を行うものとします。契約者が、当該変更を行わなかったことにより当社からの契約者に対する通知が不到達となった場合であっても、当該通知は第3条第2項に定める時点でその効力を生じます。また、当社は、契約者が前項の変更を行わなかったことにより契約者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
              '契約者は、本サービスの利用が契約者に適用のある法令又は契約者が所属する業界団体の規則に違反するか否かを自己の責任と費用負担で調査するものとします。',
              '契約者は、自らの本サービスの利用により生じた結果について、一切の責任を負うものとし、契約者による本サービスの利用その他本契約又は利用契約に関する行為に起因又は関連して第三者に損害等を生じさせた場合（契約者が本規約に違反したことにより、第三者に損害等を生じさせた場合を含みます。）又は第三者から請求、警告、問い合わせ、クレームその他の通知を受けた場合には、自己の責任と費用においてこれらを処理及び解決するものとします。また、契約者による本サービスの利用その他本契約又は利用契約に関する行為に起因又は関連して当社が第三者から請求、警告、問い合わせ、クレームその他の通知を受けた場合には、契約者は、当社に対し、これらに起因又は関連して当社に生じた損害等を補償するものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第12条（契約者保存情報のバックアップ）
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者は、契約者保存情報については、自己の責任と費用において、同一のデータをバックアップとして保存するものとします。',
              '契約者は、契約者保存情報が保存された本サービスに係る利用契約が終了する場合には、当該利用契約が終了する前に、自己の責任と費用において、当該契約者保存情報をダウンロードして取得するものとします。なお、契約者は、当該利用契約の終了後は、当該契約者保存情報を参照、閲覧、操作又は取得することができないものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第13条（禁止事項）</Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              契約者は、本サービスの利用に関し、以下の各号に掲げる行為を行わず、第三者による当該行為に関与してはならないものとします。契約者が当該行為を行った場合又は第三者による当該行為に関与した場合、契約者は、当社に対し、当該行為に起因又は関連して当社又は第三者に生じた損害等を補償するものとします。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '本サービスの運営を妨げ若しくは本サービスの提供に支障をきたす行為又はそれらのおそれのある行為',
                  '当社又はその他の第三者の知的財産権、プライバシーその他の権利を侵害する行為又はそのおそれのある行為',
                  '法令に違反し若しくは公序良俗に反する行為又はそれらのおそれのある行為',
                  '当社又は契約者が所属する業界団体の規則に違反する行為',
                  'コンピューター・ウィルスその他の有害なコンピューター・プログラムを含む情報を送信する行為',
                  '本サービスに関して契約者にアクセス権限がない当社サービス環境のシステム又は情報にアクセスし又はそれらを利用、取得若しくは改ざんする行為',
                  '本サービス環境に情報セキュリティ上の悪影響を与えることを防ぐために合理的に必要な措置を講じていないクライアント環境を利用して本サービス環境に接続する行為',
                  '当社が定める一定のデータ容量以上のデータを本サービスを通じて送信する行為',
                  '本サービスを本目的以外の目的で利用する行為',
                  'その他当社が不適当と判断する行為',
                ]}
              />
            </List>
          </ListItem>
          <ListItem>
            <ListItemText>
              当社は、契約者が前項各号のいずれかに該当する行為を行ったと判断した場合には、契約者への事前の通知なく、契約者に対する本サービスの提供を停止し、契約者保存情報の全部又は一部を削除し、その他当社が適当と判断する措置を講ずることができるものとします。
            </ListItemText>
          </ListItem>
        </List>

        <Typography variant="h2">第14条（利用料金）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本サービスの利用料金及びその算定方法（以下単に「利用料金」といいます。）は、本サービス仕様書に掲載されるとおりとします。',
              '契約者は、前項に定める利用料金並びにこれに対する消費税及び地方消費税を、当社の指定する決済方法により、当社の指摘する支払期日までに、当社に対して支払うものとします。かかる支払いに要する振込手数料は、契約者の負担とします。',
              '前項に基づき当社に対し支払われた利用料金は、本サービスの内容変更、提供終了その他いかなる理由があっても返還又は減額を行いません。',
              '当社は、利用料金を変更する場合は、2か月以上の予告期間を定めて契約者に通知します。かかる変更後の利用料金は、当該利用料金が適用される利用契約が当該予告期間の経過後に第16条第2項但書きに従い更新された場合に、当該更新後の利用契約から適用されます。',
              '契約者が、利用料金その他本契約又は利用契約に基づく当社に対する金銭債務の支払いを遅延したときは、契約者が当社に対して負担する一切の債務について期限の利益を失うものとし、当該支払いを遅延した日の翌日から支払済みに至るまで、年14.6％の割合による遅延損害金を当社に対して支払うものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第15条（本トライアルサービスの提供）
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社は、トライアル利用契約を締結した契約者に対し、本規約、本契約及びトライアル利用契約に従い、本トライアルサービスを提供するものとします。',
              '本トライアルサービスの内容は、本トライアルサービス仕様書又はサービス販売サイトに定めるとおりとします。本トライアルサービス仕様書又はサービス販売サイトに記載のない本トライアルサービスの内容の詳細は、当社がその裁量によって決定するものとします。',
              '本トライアルサービスの対価は無料とします。',
              '個別のトライアル利用契約の契約期間は、当該契約が対象とする本トライアルサービスに係る本トライアルサービス利用開始日から30日間とします。トライアル利用契約を締結した契約者が当該契約期間中に第5条第3項に従って当該本トライアルサービスと同一の当社サービスについて利用契約を申し込み当該契約が締結された場合、当該締結日をもって当該トライアル利用契約は終了しますが、当社は当該契約期間中に保存された契約者保存情報を削除せず、当該契約者は当該利用契約に基づき当該情報を利用することができます。',
              'トライアル利用契約を締結した契約者は、当該契約の対象となる本トライアルサービスと同一の当社サービスについて、再度トライアル利用契約の申込みを行うことはできません。',
              '当社は、当社の契約違反による債務不履行責任、不法行為責任その他法律上の請求原因の如何、直接かつ現実に発生した損害、間接損害、逸失利益その他発生した損害の種類及び重過失の有無を問わず、本トライアルサービス又はトライアル利用契約に関して契約者に対して損害を賠償し又は補償する責任を一切負いません。但し、当該責任免除が適用される法令により効力を有しない場合、当社が本トライアルサービス又はトライアル利用契約に関して契約者に対して負う責任については、第24条第3項乃至第5項が、「利用契約」を「トライアル利用契約」、「本サービス」を「本トライアルサービス」に読み替えて適用されるものとします。',
              '当社及びトライアル利用契約を締結した契約者には、第6条第3項乃至第8項、第8条乃至第13条並びに第24条第1項及び第2項が、「利用契約」を「トライアル利用契約」、「本サービス」を「本トライアルサービス」、「本サービス仕様書」を「本トライアルサービス仕様書」、「本サービス利用開始日」を「本トライアルサービス利用開始日」に読み替えて適用されるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第16条（契約期間）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本契約の契約期間は、本契約が成立した日から、本契約に基づき締結された全ての利用契約及びトライアル利用契約が終了するまでとします。',
              '個別の利用契約の契約期間は、当該契約が対象とする本サービスに係る本サービス利用開始日から当該利用開始日が属する月の末日までとします。但し、契約期間満了の1か月前までに、当社所定の方法による契約終了の意思表示がなされない場合、同一条件により、利用契約は1か月間更新されるものとし、以後も同様とします。',
            ]}
          />
        </List>

        <Typography variant="h2">第17条（契約者による途中解約）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者は、本契約及びトライアル利用契約の成立後いつでも、本条の定めに従い、本契約及びトライアル利用契約の全部又は一部を解約し、本契約又はトライアル利用契約を終了させることができます。契約者は、本契約及びトライアル利用契約の全部又は一部の解約を行うときには、本契約のときは解約を希望する日の1か月前までに、トライアル利用契約の解約のときは解約を希望する日の3日前までに、当社所定の方法により、解約を希望する本契約又はトライアル利用契約を明示して、当社に解約の申込みを行うものとします。本契約又はトライアル利用契約は、本契約の解約の場合は契約者から当社に解約の申込みが到達してから1か月後の日又は契約者が解約を希望する日のいずれか遅い日、トライアル利用契約の解約の場合は契約者から当社に解約の申込みが到達してから3日後の日又は契約者が解約を希望する日のいずれか遅い日を解約日として終了するものとします。契約者が本契約の解約の申込みを行った場合には、全ての利用契約及びトライアル利用契約の解約の申込みがなされたものとみなされ、本契約の終了と同時に全ての利用契約及びトライアル利用契約が終了するものとします。',
              '契約者は、個別の利用契約に係る途中解約の申込みを行うことはできません。契約者は、本契約を継続し、かつ、個別の利用契約を終了したい場合は、前条第2項に基づく個別の利用契約に係る契約終了の意思表示が必要となります。',
              '当社は、第1項に基づく解約により利用契約が終了した場合であっても、終了した利用契約に係る本サービスの利用料金の日割り計算による減額又は返金は行わず、契約者は、当該利用契約の終了日から当該終了日が属する月の末日までの期間に対応する利用料金を当社に支払う義務を負います。',
              '契約者が第1項に基づき本契約を解約し全ての利用契約を終了した後、再度終了した利用契約に係る当社サービスの利用を希望する場合には、再度、本契約の締結及び第5条第3項に従い当該当社サービスの利用の申込みを行う必要があります。この場合、契約者は、解約前の当該当社サービスに係る契約者保存情報が引き継がれないことを了承するものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第18条（当社による利用停止・解除等）
        </Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              当社は、契約者が以下の各号に掲げる事由のいずれか一つに該当することが判明した場合には、契約者への事前の通知又は催告なしに、直ちに、契約者に対する本サービス又は本トライアルサービスの全部若しくは一部の提供の停止又は本契約、利用契約若しくはトライアル利用契約の全部若しくは一部の解除を行うことができるものとし、契約者は、これらの対応について何らの異議も述べることができないものとします。当社が本契約を解除した場合、本契約の終了と同時に全ての利用契約及びトライアル利用契約が終了するものとします。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '契約者が本規約に違反し、当社が違反の是正を催告した後10営業日以内に当該違反が是正されない場合',
                  '本サービス又は本トライアルサービスの運営及び管理のために必要であると当社が合理的に判断した場合',
                  '理由の如何を問わず契約者による利用料金の支払いその他の当社に対する金銭債務の支払いの決済が適法かつ有効になされなかった場合（かかる決済が、決済方法の不正使用によりなされたものであることが判明した場合を含みます。）',
                  '契約者が成年被後見人である場合又は未成年者・被保佐人・被補助人であって法定代理人・保佐人・補助人の同意なく本サービス又は本トライアルサービスを利用した場合',
                  '契約者が死亡した場合又は後見開始、保佐開始若しくは補助開始の審判を受けた場合',
                  '契約者登録情報に虚偽の事実があることが判明した場合',
                  '手段の如何を問わず、本サービス又は本トライアルサービスの運営を妨害した場合',
                  '支払停止若しくは支払不能となり、又は破産手続開始、民事再生手続開始、会社更生手続開始、特別清算開始若しくはこれらに類する手続の開始の申立てがあった場合',
                  '自ら振出し、若しくは引受けた手形若しくは小切手につき、不渡りの処分を受けた場合、又は手形交換所の取引停止処分その他これに類する措置を受けた場合',
                  '差押、仮差押、仮処分、強制執行又は競売の申立てがあった場合',
                  '租税公課の滞納処分を受けた場合',
                  '反社会的勢力等（暴力団、暴力団員、右翼団体、反社会的勢力、その他これに準ずる者を意味します。以下同じ。）である、又は資金提供その他を通じて反社会的勢力等の維持、運営若しくは経営に協力若しくは関与する等反社会的勢力等との何らかの交流若しくは関与を行っていると当社が判断した場合',
                  'その他前各号に類する事由があると当社が判断した場合',
                ]}
              />
            </List>
          </ListItem>
          <ListTexts
            texts={[
              '前項に従い契約者に対する本サービスの全部若しくは一部の提供の停止又は本契約若しくは利用契約の全部若しくは一部の解除がなされた場合には、当社は本サービスの利用料金の日割り計算による減額又は返金は行わないものとし、また、契約者は、当社に対して負担する一切の金銭債務について期限の利益を失い、当社に対し、直ちに当該債務の全額を支払う義務を負うものとします。',
              '契約者は、第1項に従い本契約、利用契約又はトライアル利用契約が解除された場合であっても、当該本契約、利用契約又はトライアル利用契約に基づく当社及び第三者に対する何らの義務（損害賠償義務を含みますが、これに限りません。）も免れるものではありません。',
              '当社は、第1項に基づく契約者に対する本サービス又は本トライアルサービスの全部若しくは一部の提供の停止又は本契約若しくは利用契約の全部若しくは一部の解除に起因又は関連して契約者及びその他の第三者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第19条（当社による本サービス又は本トライアルサービスの廃止）
        </Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社は、本サービス又は本トライアルサービスの全部又は一部の提供を廃止することがあります。その場合、当社は、廃止されるのが本サービスのときは12か月以上の予告期間をおいて、廃止されるのが本トライアルサービスのときは当社が自らの裁量で決定する合理的な予告期間をおいて、契約者にその旨を通知するものとします。',
              '前項に定める通知がなされた場合、当該廃止の対象となる本サービスに係る利用契約又は本トライアルサービスに係るトライアル利用契約は、当該予告期間の満了をもって終了するものとします。',
              '当社は、前項に定める本サービス又は本トライアルサービスの提供の廃止に関して、契約者に損害等が生じた場合であっても、当該損害等について契約者に対して何らの責任も負わないものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第20条（当社秘密情報の取扱い）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者は、(1)当社が秘密情報である旨を指定し又は通知して契約者に提供した情報、(2)本契約及び利用契約の条件及び内容、(3)本サービスの利用に関連し又はかかる利用の過程で取得した当社の営業上又は技術上の情報、(4)ユーザーID、パスワード、本サービスの不具合の発生に係るエラーコードその他当社が本サービスの利用者に限定して提供する情報（以下総称して「当社秘密情報」といいます。）を、本サービス又は本トライアルサービスを利用する目的以外の目的で利用してはならず、当社の事前の書面による承諾なしに、本サービス又は本トライアルサービスを利用するために当社秘密情報を知る必要のある自己の役員及び従業員以外の第三者に対して提供、開示又は漏洩してはならないものとします。',
              '前項の規定にかかわらず、本サービス又は本トライアルサービスを本目的で利用するために必要な範囲に限り、契約者は、共同利用者に対して当社秘密情報を開示できるものとします。その場合、契約者は共同利用者に対して、本条に規定する契約者の義務を遵守させることにつき一切の責任を負うものとします。',
              '第1項の規定にかかわらず、(1)契約者が取得したときに既に一般に公知であったもの、(2)契約者が取得した後、契約者の責めに帰すべき事由によらずに公知となったもの、(3)第三者から秘密保持義務を負わされることなく契約者が適法に取得したもの、(4)契約者が当社から取得した情報によることなく独自に作成したものは、当社秘密情報に含まれないものとします。',
              '契約者は、当社から求められた場合には、いつでも、遅滞なく、当社秘密情報並びに当社秘密情報を記載又は包含した書面その他の記録媒体物及びその全ての複製物を返却又は廃棄するものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第21条（契約者情報の取扱い）</Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              当社は、契約者情報を、次の各号に掲げる目的で利用することができるものとし、契約者は、かかる利用について何らの異議も述べないものとします。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '当社の製品及びサービス（以下「当社製品等」といいます。）の販売、提供、運用又は保守',
                  '当社製品等のサジェスト機能の精度向上、検索結果の利便性向上その他当社製品等の機能、仕様、動作、コンテンツ等のカスタマイズ、改善又は新規開発',
                  '契約者情報の処理、編集又は加工による統計的データ、属性情報その他当社の事業のために有用な情報の作成又は利用',
                  '当社製品等のユーザー又は潜在的ユーザーへの周知又は販売促進',
                  '契約者への連絡、料金請求、契約者からの問い合わせ対応その他契約者との交信',
                  '本契約又は利用契約の違反事由又は解除事由の有無に関する調査若しくは確認',
                  '前各号の目的に付帯又は関連する範囲におけるデータ分析等（当該分析等の結果を前各号の目的で利用することを含みます。）',
                  'その他当社の事業遂行又は社内業務処理（本号は契約者関連情報にのみ適用されます。）',
                ]}
              />
            </List>
          </ListItem>
          <ListItem>
            <ListItemText>
              当社は、契約者情報を、次の各号のいずれかに該当する場合を除いて、第三者に提供又は開示しないものとします。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '契約者の書面による同意を得た場合',
                  '特定の個人又は契約者の識別ができないよう処理、編集又は加工された情報を第1項の目的で第三者に開示若しくは提供し又は一般に公開する場合',
                  '当社の親会社、子会社、当社と同一の親会社を持つ会社、関連会社又は外部委託先に対して第1項の目的で提供する又は取扱いを委託する場合',
                  '弁護士、公認会計士等の外部専門家に対して法定の守秘義務の下又は守秘義務を課して開示する場合',
                  '法令遵守のための必要な場合又は裁判所、政府機関等の要請がある場合',
                ]}
              />
            </List>
          </ListItem>
          <ListTexts
            texts={[
              '前2項の規定にかかわらず、契約者情報のうち、(1)当社が取得したときに既に一般に公知であったもの、(2)当社が取得した後、当社の責めに帰すべき事由によらずに公知となったもの、(3)第三者から秘密保持義務を負わされることなく当社が適法に取得したもの、(4)当社が契約者から取得した情報によることなく独自に作成したものについては、当社は、第1項に定める目的の制限なく利用することができるものとし、かつ、第2項に定める義務を負わないものとします。',
              '当社は、本契約が終了した場合、終了原因の如何を問わず、当社の裁量により、契約者登録情報及び契約者保存情報をいつでも削除又は廃棄することができるものとし、当該削除によって契約者に損害等が生じた場合であっても、当該損害等について、契約者に対して何らの責任も負わないものとします。また、当社は、終了原因の如何を問わず、当社の裁量により、本契約終了後も契約者登録情報及び契約者保存情報の保有を継続し、本条及び第22条に従い利用することができるものとします。',
              '当社は、本契約の終了の前後を問わず、自らの裁量により、契約者関連情報をいつでも削除又は廃棄することができます。また、当社は、終了原因の如何を問わず、当社の裁量により、本契約終了後も契約者関連情報の保有を継続し、本条及び第22条に従い利用することもできます。',
            ]}
          />
        </List>

        <Typography variant="h2">第22条（個人情報の取り扱い）</Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              当社は、契約者情報に含まれる個人情報その他の本サービス又は本トライアルサービスの提供のために契約者から取得した個人情報を、別途サービス販売サイト又は契約者専用サイトに掲示する
              <Link href={'https://www.worksap.co.jp/privacypolicy/'}>
                「個人情報保護方針」
              </Link>
              及び個人情報保護法に基づき、情報セキュリティを確保するための適切な措置を講じて取扱います。
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              当社は、前項の個人情報について、第21条第2項の要件を満たす場合は第三者に提供できますが、その場合でも、個人情報保護法を遵守し同法の要件を満たす必要があります。
            </ListItemText>
          </ListItem>
        </List>

        <Typography variant="h2">第23条（知的財産権）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '当社サービスの提供のために当社が使用するソフトウェア、ドキュメントその他一切のコンテンツ（以下総称して「ソフトウェア等」といいます。）の著作権その他一切の知的財産権は、当社又は当該知的財産権に係るライセンスを当社に許諾したライセンサーである第三者に帰属します。契約者は、当該ソフトウェア等のリバースエンジニアリング、逆コンパイル若しくは逆アセンブルを行ってはならず、当該ソフトウェア等の全部若しくは一部について複製、公衆送信（送信可能化を含みます。）、修正、変更、改変若しくは翻案し、又は第三者に開示してはなりません。',
              '当社は、本サービス又は本トライアルサービスの提供に際して、契約者に対し、本サービス又は本トライアルサービスを利用するために必要なクライアント環境にて動作するソフトウェアの利用を許諾する場合があります。かかるソフトウェアの導入及び利用に関する条件は、本サービス仕様書、本トライアルサービス仕様書又は当社所定の利用許諾条件に定められるものとし、契約者はこれらの定めに従うものとします。',
              'サービス販売サイト、契約者専用サイトその他当社のウェブサイトには商標、ロゴ及びサービスマーク（以下総称して「商標等」といいます。）が表示される場合がありますが、当社は、契約者その他の第三者に対し、当該商標等の使用その他当該商標等に関する何らの権利も許諾するものではありません。',
              '契約者は、当社サービスに関わる発明その他の知的財産に関し特許権、実用新案権、意匠権、商標権の出願を申請し又は著作権に関する登録を申請してはならないものとします。',
              '契約者が前各項の定めに違反して何らかの問題が生じた場合には、契約者は、自己の責任と費用において当該問題を処理及び解決するものとします。',
              '当社は、第21条第2項にかかわらず、当社サービスの導入企業の公表のために、契約者の名称を公開することができるものとします。この場合、当社は当該公表に必要な範囲で契約者の商標等を無償で使用できるものとします。但し、契約者は、当該名称の公開又は商標等の使用の停止又は削除を当社に請求できるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">
          第24条（契約者の責任の範囲及び損害賠償の制限）
        </Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              当社は、本サービスについて、以下の各号に掲げる事項を何ら保証するものではなく、当該事項について何らの義務も負うものではありません。
            </ListItemText>
            <List component="ol">
              <ListTexts
                texts={[
                  '契約者による本サービスの利用が、契約者に適用のある法令又は業界団体の内部規則に適合すること',
                  '契約者による本サービスの利用が、第三者の知的財産権その他の権利を侵害しないこと',
                  '本サービス仕様書に明記されたものを除き、本サービス及び本サービスの提供に利用されるソフトウェア等が、契約者の特定の利用目的に適合し又は特定の機能若しくは品質を備えていること',
                  '本サービスの提供に利用されるソフトウェアその他本サービス環境を構成するソフトウェア及びハードウェアに瑕疵若しくは破損が存在せず又は障害が発生せず、それらが特定の水準の信頼性、安全性及び効率性を備えること',
                  '本サービス環境において、契約者情報の漏洩、消失又は棄損その他情報セキュリティ上の問題又は事故が生じず、契約者情報の機密性、完全性及び可用性が常に維持されること',
                  '本サービスの提供が中断若しくは停止し又は本サービスが利用不能となる事態が生じないこと',
                ]}
              />
            </List>
          </ListItem>
          <ListTexts
            texts={[
              'サービス販売サイト若しくは契約者専用サイトから第三者のウェブサイトへのリンク又は第三者のウェブサイトからこれらのサイトへのリンクが提供されている場合であっても、当社は、当該第三者のウェブサイト及びその内容について、何らの責任も負わないものとします。',
              '前各項に定める他、当社は、本サービスに関して契約者に損害等が生じた場合であっても、当社の故意又は重過失による場合を除き、当該損害等について、契約者に対して何らの責任も負わないものとします。',
              '債務不履行責任、不法行為責任その他法律上の請求原因の如何を問わず、当社が、本サービス又は本契約若しくは利用契約に関して契約者に対して損害を賠償し又は補償する責任を負う場合、かかる責任の範囲は、契約者に直接かつ現実に発生した通常の損害に限定され、かつ、損害の事由が生じた時点から遡って直近1ヶ月の期間に契約者から現実に受領した本サービスの利用料金の総額（当該期間中に本サービスの利用料金を受領していない場合には1000円）を上限とします。なお、契約者又は第三者に発生した機会逸失、業務の中断その他これらに類する間接損害及び逸失利益は、これらが発生する可能性を当社が認識し又は契約者から事前に知らされていた場合であっても、本項に定める「直接かつ現実に発生した通常の損害」には含まれないものとします。',
              '本条各項の規定にかかわらず、当社が利用契約上負う本サービス提供義務の履行の過程でAWSその他の当社の業務委託先の責に帰すべき事由により契約者又は第三者に損害が生じた場合、当社は、当該業務委託先の選定について当社に故意又は重過失がある場合に限り、当該損害を契約者に対して賠償するものとします。なお、本項に基づく責任についても、前項に定める当社の責任の制限に関する規定が適用されるものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第25条（譲渡禁止）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '契約者は、当社の事前の承諾なく、本契約、利用契約若しくはトライアル利用契約上の地位又はこれらの契約に基づく権利義務の全部若しくは一部を、第三者に貸与、譲渡（合併又は会社分割による包括承継も含みますが、これらに限られません。）、名義変更し、又は担保の目的に供することはできません。',
              '当社は、当社が当社サービスについての事業を第三者に承継させる場合（事業譲渡、合併又は会社分割による場合を含みますが、これらに限られません。）には、本契約、利用契約又はトライアル利用契約上の地位、これらの契約に基づく権利及び義務並びに契約者情報及び個人情報を譲受人に承継させることができるものとし、契約者は、かかる承継について予め同意したものとみなします。',
            ]}
          />
        </List>

        <Typography variant="h2">第26条（完全合意）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本契約、利用契約及びトライアル利用契約は、これらの契約の成立日における本規約、本サービス又は本トライアルサービスに関する契約者及び当社間の合意を規定するものであり、口頭又は書面を問わず、これらの契約の締結前に両当事者間でなされた協議内容、合意事項又は一方当事者から相手方当事者に提供された資料、申入れその他の通信がこれらの契約の内容と相違する場合は、これらの契約が優先されるものとします。',
              '本規約に記載されている内容は、本契約、利用契約及びトライアル利用契約並びに本サービス及び本トライアルサービスに関する契約者及び当社間の合意事項の全てであり、契約者及び当社は、本契約、利用契約及びトライアル利用契約並びに本サービス及び本トライアルサービスに関し、互いに本規約で定められている内容以上の義務及び責任を負担しないものとします。',
            ]}
          />
        </List>

        <Typography variant="h2">第27条（本規約の有効性及び個別性）</Typography>
        <List component="ol">
          <ListTexts
            texts={[
              '本規約のいずれかの条項又は一部が、法令等により無効又は執行不能と判断された場合であっても、本規約の残りの規定及び一部が無効又は執行不能と判断された規定の残りの部分は、継続して完全に効力を有し、当社及び契約者は、当該無効若しくは執行不能の条項又は部分を適法とし、執行力を持たせるために必要な範囲で修正し、当該無効若しくは執行不能な条項又は部分の趣旨並びに法律的及び経済的に同等の効果を確保できるように努めるものとします。',
              '本規約のいずれかの条項又は一部が、ある契約者との関係で無効とされ、又は取り消された場合でも、当該条項又は一部は、その他の契約者との関係では有効とします。',
            ]}
          />
        </List>

        <Typography variant="h2">第28条（存続条項）</Typography>
        <Typography>
          本規約第4条第6項、第8条第4項、第11条第2項、第4項、第5項及び第7項、第12条第2項、第13条第2項、第14条第3項及び第5項、第15条第5項乃至第7項、第17条第2項及び第3項、第18条第2項乃至第4項、第19条第2項及び第3項、並びに第20条乃至第30条の規定は、本契約、利用契約又はトライアル利用契約の終了後も有効に存続するものとします。
        </Typography>

        <Typography variant="h2">第29条（準拠法）</Typography>
        <Typography>
          本規約、本契約、利用契約及びトライアル利用契約に関する一切の事項については、日本国法を準拠法とし、本規約、本契約、利用契約及びトライアル利用契約は、日本国法に従って解釈されるものとします。
        </Typography>

        <Typography variant="h2">第30条（合意管轄裁判所）</Typography>
        <Typography>
          本規約、本契約、利用契約及びトライアル利用契約に関連して、契約者と当社の間で問題が生じた場合、両者は誠意を持って協議するものとし、協議しても解決しない場合には東京地方裁判所を第一審の専属的合意管轄裁判所とします。
        </Typography>

        <Typography variant="h2">付則</Typography>
        <Typography>
          改正後の本規約は、2022年3月7日より効力を有するものとします。
        </Typography>
        <Typography className={classes.lineBreak}>
          <span>第1回改正施行日：2020年12月7日</span>
          <span>第2回改正施行日：2021年2月18日</span>
          <span>第3回改正施行日：2021年4月1日</span>
          <span>第4回改正施行日：2021年6月9日</span>
          <span>第5回改正施行日：2021年7月12日</span>
          <span>
            <Link component={RouterLink} to="/terms-archive/06-20210802">
              第6回改正施行日：2021年8月2日
            </Link>
          </span>
          <span>
            <Link component={RouterLink} to="/terms-archive/07-20211223">
              第7回改正施行日：2021年12月23日
            </Link>
          </span>
          <span>第8回改正施行日：2022年3月7日</span>
        </Typography>

        <Typography variant="h2">別表</Typography>
        <Typography>HUE Works Suiteシリーズ</Typography>
        <List>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Enterprise Spreadsheet（
              <Link href="https://bizapp.worksap.co.jp/ess/">
                https://bizapp.worksap.co.jp/ess/
              </Link>
              ）
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Project Board（
              <Link href="https://bizapp.worksap.co.jp/pjb/">
                https://bizapp.worksap.co.jp/pjb/
              </Link>
              ）
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Project Room（
              <Link href="https://bizapp.worksap.co.jp/pjr/">
                https://bizapp.worksap.co.jp/pjr/
              </Link>
              ）
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Workflow（
              <Link href="https://bizapp.worksap.co.jp/workflow/">
                https://bizapp.worksap.co.jp/workflow/
              </Link>
              ）
            </ListItemText>
          </ListItem>
        </List>
        <Typography>HUE Works Suite DX Solutionsシリーズ</Typography>
        <List>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Electronic Book Maintenance（
              <Link href="https://bizapp.worksap.co.jp/ebm/">
                https://bizapp.worksap.co.jp/ebm/
              </Link>
              ）
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText className={classes.wordBreak}>
              Chatbot（
              <Link href="https://bizapp.worksap.co.jp/chatbot/">
                https://bizapp.worksap.co.jp/chatbot/
              </Link>
              ）
            </ListItemText>
          </ListItem>
        </List>
      </>
    );
  };

  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <TermsTemplate pageTitle={pageTitle} from={validatedFrom}>
        <TermsContents />
      </TermsTemplate>
    </>
  );
};

export default Terms;
